import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { UtilitiesApi } from "@genesys/cloud-frontend/apis/UtilitiesApi";
import { RootState } from "app/store";
import { isNonProdRegion } from "util/envUtils";
import FeatureToggle from "enums/FeatureToggle";
import { API_CONFIGURATION } from "util/apiUtils";

const featureToggleKeys = Object.values(FeatureToggle);

type FeatureToggleState = {
    status: "idle" | "pending" | "finished" | "failed";
    data: Record<FeatureToggle, boolean>;
};

export const fetchToggles = createAsyncThunk(
    "featureToggle/fetchToggles",
    async () => {
        const utilitiesApi = new UtilitiesApi(API_CONFIGURATION);
        return utilitiesApi
            .getFeaturetoggles({
                feature: featureToggleKeys,
            })
            .then((result) => {
                if (isNonProdRegion()) {
                    try {
                        const localStorageToggles = JSON.parse(
                            localStorage.getItem("featureToggleOverrides") ??
                                "{}"
                        );
                        return { ...result, ...localStorageToggles };
                    } catch (err) {
                        console.error(
                            "Unable to parse feature toggle overrides."
                        );
                    }
                }

                return result;
            }) as Promise<Record<FeatureToggle, boolean>>;
    }
);

export const slice = createSlice({
    name: "featureToggle",
    initialState: {
        status: "idle",
        data: Object.fromEntries(featureToggleKeys.map((key) => [key, false])),
    } as FeatureToggleState,
    reducers: {
        toggle(state, { payload }: PayloadAction<FeatureToggle>) {
            state.data[payload] = !state.data[payload];
        },
    },
    extraReducers(builder) {
        builder
            .addCase(fetchToggles.pending, (state) => {
                state.status = "pending";
            })
            .addCase(fetchToggles.fulfilled, (state, { payload }) => {
                state.status = "finished";
                state.data = payload;
            })
            .addCase(fetchToggles.rejected, (state) => {
                state.status = "failed";
            });
    },
});

export const { toggle } = slice.actions;

export const selectFeatureToggles = (state: RootState) =>
    state.featureToggle.data;

export const selectFeatureToggle = (state: RootState, feature: FeatureToggle) =>
    selectFeatureToggles(state)[feature];

export const selectFeatureTogglesLoaded = (state: RootState) =>
    state.featureToggle.status === "finished";

export default slice.reducer;
